import {Account} from './account.model';
import {Resource} from './resource.model';

export enum OrganizationStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum CompanySize {
  SMALL = '1-10',
  MEDIUM_SMALL = '11-50',
  MEDIUM = '51-200',
  MEDIUM_LARGE = '201-500',
  LARGE = '501-1000',
  ENTERPRISE = '1000+',
}

export class Organization {
  // Required fields
  name: string;
  createdByAccount: Account;
  logoImg: Resource;
  status: OrganizationStatus;
  industry: string;
  companyUrl: string;
  companySize: CompanySize;
  description: string;
  coverImage: Resource;
  portalUrl: string;

  // Legacy/additional fields (keeping for backward compatibility)
  active: boolean;
  createdBy: string;
  id: string;
  createdAt?: string;
  updatedAt?: string;

  constructor(json: any) {
    // Required new fields
    this.name = json.name;
    this.createdByAccount = json.createdByAccount ? new Account(json.createdByAccount) : new Account({});
    this.logoImg = json.logoImg ? new Resource(json.logoImg) : new Resource({});
    this.status = json.status || OrganizationStatus.ACTIVE;
    this.industry = json.industry || '';
    this.companyUrl = json.companyUrl || '';
    this.companySize = json.companySize || CompanySize.SMALL;
    this.description = json.description || '';
    this.coverImage = json.coverImage ? new Resource(json.coverImage) : new Resource({});
    this.portalUrl = json.portalUrl || '';

    // Legacy fields
    this.active = json.active;
    this.createdBy = json.createdBy;
    this.id = json.id || json._id;
    this.createdAt = json.createdAt;
    this.updatedAt = json.updatedAt;
  }

  getDisplayName(): string {
    return this.name;
  }

  hasLogo(): boolean {
    return !!this.logoImg && !!this.logoImg.url;
  }

  hasCoverImage(): boolean {
    return !!this.coverImage && !!this.coverImage.url;
  }

  isActive(): boolean {
    return this.status === OrganizationStatus.ACTIVE || this.active;
  }

  getSubdomain(): string {
    return this.portalUrl || '';
  }

  getLogoUrl(): string {
    return this.logoImg?.url || '';
  }

  getCoverImageUrl(): string {
    return this.coverImage?.url || '';
  }

  getCompanySizeLabel(): string {
    switch (this.companySize) {
      case CompanySize.SMALL:
        return '1-10 employees';
      case CompanySize.MEDIUM_SMALL:
        return '11-50 employees';
      case CompanySize.MEDIUM:
        return '51-200 employees';
      case CompanySize.MEDIUM_LARGE:
        return '201-500 employees';
      case CompanySize.LARGE:
        return '501-1000 employees';
      case CompanySize.ENTERPRISE:
        return '1000+ employees';
      default:
        return '';
    }
  }

  hasWebsite(): boolean {
    return !!this.companyUrl && this.companyUrl.trim() !== '';
  }

  getFormattedWebsiteUrl(): string {
    if (!this.companyUrl) return '';
    return this.companyUrl.startsWith('http') ? this.companyUrl : `https://${this.companyUrl}`;
  }
}

export enum AccountOrganizationRole {
  ADMIN = 'ADMIN',
  RECRUITER = 'RECRUITER',
  EMPLOYEE = 'EMPLOYEE',
}

export class AccountOrganization {
  accountId: Account;
  active: boolean;
  id: string;
  organization: Organization;
  credCount: number;
  reccoCount: number;
  role: AccountOrganizationRole;
  createdAt: string;
  updatedAt: string;

  constructor(json: any) {
    this.accountId = json.accountId && new Account(json.accountId);
    this.active = json.active;
    this.id = json.id || json._id;
    this.organization = json.organization ? new Organization(json.organization) : new Organization({});
    this.credCount = json.credCount || 0;
    this.reccoCount = json.reccoCount || 0;
    this.role = json.role || AccountOrganizationRole.EMPLOYEE;
    this.createdAt = json.createdAt;
    this.updatedAt = json.updatedAt;
  }

  isAdmin(): boolean {
    return this.role === AccountOrganizationRole.ADMIN;
  }

  isRecruiter(): boolean {
    return this.role === AccountOrganizationRole.RECRUITER;
  }

  isEmployee(): boolean {
    return this.role === AccountOrganizationRole.EMPLOYEE;
  }

  canRecruit(): boolean {
    return this.role === AccountOrganizationRole.ADMIN || this.role === AccountOrganizationRole.RECRUITER;
  }

  getDisplayRole(): string {
    switch (this.role) {
      case AccountOrganizationRole.ADMIN:
        return 'Administrator';
      case AccountOrganizationRole.RECRUITER:
        return 'Recruiter';
      case AccountOrganizationRole.EMPLOYEE:
        return 'Employee';
      default:
        return 'Employee';
    }
  }
}
